import { Skill } from "../types/prisma-enums";
import type { GameContent, Item, Level } from "./types";

/**
 * Bridge Patterns — Matching & Sorting · games-spec.md §8.
 *
 * A rope bridge with its planks missing. A run of pictures is shown with one
 * piece taken out of it; the child picks the missing piece out of a shuffled
 * tray, carries it into the gap, and one plank goes down. Seven planks and the
 * panda waiting on the near bank can walk to school.
 *
 * 🔴 EVERY LEVEL IS A PATTERN NOW, WHICH IS A CONTENT REVERSAL WORTH NAMING.
 * L1 used to be "which one is the same as this", L2 "which one belongs with
 * this", L3 a mix of sorting rows and pattern rows. On the founder's instruction
 * the game asks ONE question at three difficulties — *complete the run* —
 * because that is the thing the bridge itself depicts: a line of planks with a
 * hole in it. Matching and sorting are still what the child does; they are now
 * what the pattern MAKES them do rather than a separate row shape.
 *
 * 🔴 NCF-FS puts matching, sorting and patterns among the **pre-number**
 * competencies: identify and extend *before* symbols. Drag-and-drop is the
 * natural physical expression of "put this with that", and the reach-to-carry
 * step is what keeps it from collapsing into a pure cursor game.
 *
 * 🔴 THE PIECES ARE SKY QUEST'S PICTURES, NOT KOLAM LINE ART, AND THAT IS ALSO
 * AN INSTRUCTION RATHER THAN A DRIFT. The kolam vocabulary was chosen so that
 * *"every pair is distinguishable by outline alone"* and it met that bar — but a
 * three-year-old telling `pookalam-ring` from `kolam-loop` is telling two
 * abstractions apart, while a mango from a tree is telling two THINGS apart. The
 * atlas Sky Quest already ships (`cueAtlas.ts`) is painted artwork of exactly
 * such things, and reusing it costs no new art and no new download.
 *
 * ⚠️ THE OLD ARGUMENT IS LEFT STANDING ABOVE RATHER THAN DELETED, because the
 * next person here should see the trade rather than rediscover it: what was
 * given up is the cultural rooting the kolam set carried. `mango`, `parrot` and
 * `kite` carry it now, and they carry it the way §5.1 asked — *"rooted without
 * requiring cultural knowledge to play"*.
 *
 * ⚠️ THE MALAYALAM COLUMN OF THIS TABLE IS GONE (ADR-044).
 */

/* ── the piece vocabulary ───────────────────────────────────────────────────
 * Eleven pictures and four colour chips: what a 3-year-old can tell apart at
 * arm's length, across a room, on a school laptop.
 *
 * 🔴 EVERY ONE OF THESE HAS ARTWORK AND `carry.test.ts` HOLDS THIS FILE TO IT.
 * A typo'd id renders as a dashed placeholder — a child asked to match nothing,
 * which looks exactly like a child who cannot match. `mango`, `tree`, `apple`,
 * `ball`, `fish`, `kite`, `sun`, `parrot`, `cat` and `dog` are cells of the cue
 * atlas; `plate` and the four chips are drawn by `GameArt`.
 *
 * ⚠️ THE CHIPS ARE PIECES AMONG PIECES, NOT A COLOUR TEST. No run below is built
 * out of chips alone — each one alternates a chip with a SHAPE — so a
 * colour-blind child answers on shape and never meets a question that has no
 * answer for them.
 */
export const MOTIFS = [
  "mango",
  "tree",
  "apple",
  "ball",
  "fish",
  "kite",
  "sun",
  "parrot",
  "cat",
  "dog",
  "color-red",
  "color-yellow",
  "color-blue",
  "color-green",
] as const;

export type Motif = (typeof MOTIFS)[number];

/** The gap in a pattern run — what the child is carrying a piece into. */
export const GAP = "gap";

/**
 * The seven ways of asking the one question.
 *
 * 🔴 SEVEN, BECAUSE A ROUND IS SEVEN ITEMS. `sampleRound` de-duplicates a
 * sitting by the SPOKEN LINE rather than by item id — two rows a child hears as
 * one question count as one question — so a pool with three sentences in it
 * would leave every round repeating itself. Seven distinct lines is the smallest
 * number at which a full round can be asked without a child hearing one twice.
 */
/**
 * 🔴 EVERY PROMPT NAMES THE GOAL, NOT JUST THE PUZZLE — founder instruction.
 *
 * These lines used to ask about the puzzle alone: *"What comes next?"*,
 * *"Finish the pattern"*. Correct, and missing the point of the game. The child
 * is mending a bridge so the panda can walk across it to school, and a
 * four-year-old told only *"finish the pattern"* is being given homework; one
 * told *"…so the panda reaches school"* is being given a reason.
 *
 * ⚠️ THE ASK STAYS BEFORE THE GOAL IN EVERY LINE. The school is motivation, not
 * the task — a prompt leading with it would bury the one piece of information
 * the child needs in order to act.
 *
 * ─── 🔴 ONE TABLE, ONE STORY, ALL THREE LEVELS — FOUNDER INSTRUCTION ────────
 *
 * There used to be three: `PROMPTS` for a child going to school, `SISTER_PROMPTS`
 * for a boy going to his sister, `MARKET_PROMPTS` for an aunty going to market,
 * picked by level through a `scene/theme.ts` that is now deleted. Three stories
 * is three things a four-year-old has to be told, and the SCREEN never told
 * them any of it: the walker has been `panda.glb` at every level since the 3D
 * scene landed (`three/bridge/assets.ts`), and the far bank has been the school
 * model. So L2 showed a panda walking to a school while the voice said *"his
 * sister"*, and L3 said *"Auntie"* over the same panda.
 *
 * 🔴 THE PICTURE IS THE ONE THAT CANNOT LIE, so the words were moved to it
 * rather than the other way round. One scenario — help the panda reach school —
 * at every level, and the difficulty lives entirely in the PATTERN, which is the
 * only thing the three levels were ever meant to differ in.
 *
 * ⚠️ THE AUDIO KEYS STAY PER LEVEL (`bp_l1_p_a`, `bp_l2_p_a`, `bp_l3_p_a`) EVEN
 * THOUGH THE TEXT IS NOW SHARED. They are cache keys as much as line names —
 * `tts.controller.ts` serves `immutable, max-age=31536000` — so collapsing them
 * would buy one synthesis and cost the ability to re-theme a single level ever
 * again. Three keys saying one sentence is the cheap side of that trade.
 *
 * ⚠️ AND THE ASK ITSELF WAS REWRITTEN AFTERWARDS — see the note on `PROMPTS`.
 * Naming the goal fixed *why* a child should act and left *what to do* in words
 * they do not have. Both halves were needed and only one had been done.
 *
 * ⚠️ CHANGING THIS TEXT MEANS RE-RUNNING `npm run voice:lines`. The backend
 * speaks from a GENERATED `audioKey → text` table, so an edit here that is not
 * regenerated leaves the game saying the old sentence with nothing to notice.
 *
 * 🔴 THE `bp_*.mp3` FILES ON DISK WERE ORPHANS AND ARE NOW DELETED. They were
 * named `bp_l1_a`, while every key in this file is `bp_l1_p_a` — so not one of
 * them had ever been played, and Bridge Patterns was as silent as Sky Quest for
 * the same reason. Keeping stale files that match no key is worse than having
 * none: `audio:check` counts them as coverage that does not exist.
 */
/*
 * ─── 🔴 REWRITTEN AS A SITUATION, BECAUSE "PATTERN" IS NOT A WORD A THREE-YEAR-
 *     OLD OWNS ────────────────────────────────────────────────────────────────
 *
 * Every line used to open with *"Match the pattern"*, *"What goes in the gap"*
 * or *"Which piece is missing?"* — and `pattern` and `gap` are the names of
 * IDEAS. A child can point at a hole in a bridge; they cannot point at a gap in
 * a pattern. The founder's report was the plain version: *"whatever task you
 * give, the kid will not be able to understand — give the task scenario-based,
 * [they are] just in pre-school"*.
 *
 * 🔴 THE GAME NEVER NEEDED THE WORD. It teaches mango-mango-?-mango perfectly
 * well by showing it; naming the category bought nothing and cost the whole
 * instruction. So these describe what is on screen — a bridge with a hole in it
 * — and what to do about it, in words a pre-reader already has.
 *
 * ⚠️ AND THEY ARE TWO SHORT SENTENCES RATHER THAN ONE LONG ONE. These are SPOKEN
 * to a pre-reader; a full stop is where the voice breathes, and it is a place
 * for a four-year-old to catch up.
 *
 * 🔴 SIXTY-SIX CHARACTERS IS THE CEILING, AND IT IS ENFORCED — `width.test.ts`
 * fails the build for a prompt that cannot fit the prompt column in two lines,
 * and it caught the first draft of every line below. The scene has to be paid
 * for out of the same budget as the ask, which is why it is five words and not a
 * sentence: *"the bridge has a hole"* does the whole job of situating the child,
 * and anything longer would have cost the goal clause at the end.
 *
 * ⚠️ NO LINE NAMES THE ANSWER, and none may. The piece that fits is the whole
 * question — a prompt saying *"put the mango in"* would be reading the answer
 * out to the child, which is the one way to make this table worthless.
 */
const PROMPTS = [
  {
    en: "The bridge has a hole. Fill it, so the panda reaches school.",
  },
  {
    en: "One piece is missing. Find it, so the panda reaches school.",
  },
  {
    en: "Which piece fits the hole? Put it in, so the panda reaches school.",
  },
  {
    en: "Put the missing piece in the bridge, so the panda reaches school.",
  },
  {
    en: "A piece fell out. Put a new one in, so the panda reaches school.",
  },
  {
    en: "Choose the piece that fits the hole, so the panda reaches school.",
  },
  {
    en: "Fix the hole in the bridge, so the panda reaches school.",
  },
] as const;

/**
 * Terse constructor: these tables are read far more often than written.
 *
 * `p` indexes `PROMPTS`; `sequence` is the run with `GAP` where the plank goes;
 * `choices` is the shuffled tray in the order it is drawn — authored so the
 * answer is not always the same tile, because a child learns "the middle one"
 * faster than they learn the pattern; `target` completes the run.
 */
const run = (
  id: string,
  audioKey: string,
  p: number,
  sequence: string[],
  choices: string[],
  target: string,
): Item => ({ id, prompt: PROMPTS[p], audioKey, target, choices, sequence });

/* ── L1 · the same piece, again ──────────────────────────────────────────────
 * One picture repeated with the third lifted out: `mango · mango · _ · mango`.
 * There is no rule to work out — the run says what it is made of, four times
 * over — so the whole ask is *find that piece in the tray*. That is where a
 * 3-year-old succeeds on their first carry, and it is the sameness L1 always
 * tested, now shaped like the bridge.
 *
 * 🔴 TWO PIECES IN THE TRAY, NEVER ONE. These rows were authored with a
 * single-piece tray, and that is not an easier question — it is not a question
 * at all: the only tile is the answer, so a child who has understood nothing
 * carries the right plank every time and the level reports them as having
 * learnt the pattern. `validatePack.ts` rejects a one-choice item outright and
 * two contract tests demand at least two, so it never reached a classroom — but
 * it is the failure mode to watch for the next time a level is simplified.
 *
 * ⚠️ TWO RATHER THAN L2's THREE, because the step this level tests is *carry a
 * piece to a hole*, not *choose between pieces*. One wrong tile is what makes
 * the choice real; a second wrong tile only makes the carry longer.
 *
 * ⚠️ THE DISTRACTOR IS CHOSEN FOR OUTLINE, NOT FOR CATEGORY. `apple` against
 * `ball` is two circles at arm's length on a school laptop, which tests
 * eyesight rather than pattern; `apple` against `kite` is tellable across a
 * room. `plate` follows its own note in the vocabulary above and stays away
 * from `ball`, `apple` and `sun`.
 *
 * ⚠️ AND THE ANSWER ALTERNATES SIDES DOWN THE TABLE — seven rows lead with it,
 * seven follow it. A tray whose answer is always the left tile teaches *left*,
 * and a child learns that faster than they learn the pattern.
 */
const L1: Level = {
  level: 1,
  name: { en: "Same again" },
  objective: {
    en: "Find the piece the row is made of, and carry it into the gap",
  },
  ncf: ["C-8.1"],
  /*
   * 🔴 SIX, BECAUSE THE BRIDGE HAS SIX PLANKS.
   *
   * This number is no longer only a round length — it is the SPAN. Every correct
   * placement lays one plank across the river, so `itemCount` and the number of
   * planks are the same quantity seen twice, and `crossing/bridgeSpan.test.ts`
   * asserts the two agree — per level, because this number is authored three
   * times and a content edit only ever touches one of them. Change one and the
   * other must move with it, or a child answers every question and is left
   * standing at a bridge with a hole in it.
   */
  itemCount: 6,
  motorAction: { kind: "grabPlace" },
  items: [
    run(
      "bp.l1.a",
      "bp_l1_p_a",
      0,
      ["mango", "mango", GAP, "mango"],
      ["mango", "tree"],
      "mango",
    ),
    run(
      "bp.l1.b",
      "bp_l1_p_b",
      1,
      ["tree", "tree", GAP, "tree"],
      ["fish", "tree"],
      "tree",
    ),
    run(
      "bp.l1.c",
      "bp_l1_p_c",
      2,
      ["apple", "apple", GAP, "apple"],
      ["apple", "kite"],
      "apple",
    ),
    run(
      "bp.l1.d",
      "bp_l1_p_d",
      3,
      ["ball", "ball", GAP, "ball"],
      ["parrot", "ball"],
      "ball",
    ),
    run(
      "bp.l1.e",
      "bp_l1_p_e",
      4,
      ["fish", "fish", GAP, "fish"],
      ["fish", "sun"],
      "fish",
    ),
    run(
      "bp.l1.f",
      "bp_l1_p_f",
      5,
      ["kite", "kite", GAP, "kite"],
      ["dog", "kite"],
      "kite",
    ),
    run(
      "bp.l1.g",
      "bp_l1_p_g",
      6,
      ["sun", "sun", GAP, "sun"],
      ["sun", "cat"],
      "sun",
    ),
    run(
      "bp.l1.h",
      "bp_l1_p_h",
      0,
      ["parrot", "parrot", GAP, "parrot"],
      ["mango", "parrot"],
      "parrot",
    ),
    run(
      "bp.l1.i",
      "bp_l1_p_i",
      1,
      ["cat", "cat", GAP, "cat"],
      ["cat", "kite"],
      "cat",
    ),
    run(
      "bp.l1.j",
      "bp_l1_p_j",
      2,
      ["dog", "dog", GAP, "dog"],
      ["mango", "dog"],
      "dog",
    ),
    /* ⚠️ NO TWO ROUND PIECES IN ONE QUESTION. `apple`, `ball` and `sun` are all a
     * circle at arm's length, and so is a colour chip beside `ball` — rows that
     * paired them (l1.k, l2.a, l2.j, l3.a, l3.e, l3.n) asked a child to tell
     * circles apart rather than read the row. `kite` and `dog` took their place.
     *
     * The chips enter here, one row each, against picture distractors — the
     * header's rule: a chip is never asked about among other chips.
     *
     * ⚠️ `k` WAS THE PLATE ROW AND IS NOW THE FOURTH CHIP. Retiring `plate` from
     * the vocabulary left L1 one row short of its own rule — every other motif
     * has exactly one row — and the obvious substitution made it an exact
     * duplicate of `bp.l1.a`, which `sampleRound` cannot see: it de-duplicates a
     * sitting by the SPOKEN LINE, and these two would have had different lines
     * and identical rows. `color-green` was the one piece in the vocabulary with
     * no row of its own, so it takes this one and the chip set is complete. */
    run(
      "bp.l1.k",
      "bp_l1_p_k",
      3,
      ["color-green", "color-green", GAP, "color-green"],
      ["kite", "color-green"],
      "color-green",
    ),
    run(
      "bp.l1.l",
      "bp_l1_p_l",
      4,
      ["color-red", "color-red", GAP, "color-red"],
      ["fish", "color-red"],
      "color-red",
    ),
    run(
      "bp.l1.m",
      "bp_l1_p_m",
      5,
      ["color-yellow", "color-yellow", GAP, "color-yellow"],
      ["color-yellow", "cat"],
      "color-yellow",
    ),
    run(
      "bp.l1.n",
      "bp_l1_p_n",
      6,
      ["color-blue", "color-blue", GAP, "color-blue"],
      ["tree", "color-blue"],
      "color-blue",
    ),
  ],
};

/* ── L2 · two, taking turns ──────────────────────────────────────────────────
 * `mango · plate · mango · _`. Two pictures alternating, gap at the END of the
 * run — the easier gap, because the child reads forward from what is already
 * there and never holds both sides of a hole in mind at once. That is L3's ask.
 */
const L2: Level = {
  level: 2,
  name: { en: "Two by two" },
  objective: {
    en: "Two pieces taking turns — carry the one that comes next",
  },
  ncf: ["C-8.1", "C-8.2"],
  itemCount: 6,
  motorAction: { kind: "grabPlace" },
  items: [
    run(
      "bp.l2.a",
      "bp_l2_p_a",
      0,
      ["mango", "apple", "mango", GAP],
      ["tree", "apple", "kite"],
      "apple",
    ),
    run(
      "bp.l2.b",
      "bp_l2_p_b",
      1,
      ["tree", "ball", "tree", GAP],
      ["ball", "fish", "mango"],
      "ball",
    ),
    run(
      "bp.l2.c",
      "bp_l2_p_c",
      2,
      ["fish", "kite", "fish", GAP],
      ["cat", "sun", "kite"],
      "kite",
    ),
    run(
      "bp.l2.d",
      "bp_l2_p_d",
      3,
      ["sun", "cat", "sun", GAP],
      ["cat", "dog", "mango"],
      "cat",
    ),
    run(
      "bp.l2.e",
      "bp_l2_p_e",
      4,
      ["apple", "dog", "apple", GAP],
      ["parrot", "apple", "dog"],
      "dog",
    ),
    run(
      "bp.l2.f",
      "bp_l2_p_f",
      5,
      ["parrot", "mango", "parrot", GAP],
      ["mango", "kite", "tree"],
      "mango",
    ),
    run(
      "bp.l2.g",
      "bp_l2_p_g",
      6,
      ["color-red", "mango", "color-red", GAP],
      ["tree", "mango", "color-red"],
      "mango",
    ),
    run(
      "bp.l2.h",
      "bp_l2_p_h",
      0,
      ["mango", "fish", "mango", GAP],
      ["fish", "sun", "cat"],
      "fish",
    ),
    run(
      "bp.l2.i",
      "bp_l2_p_i",
      1,
      ["color-yellow", "tree", "color-yellow", GAP],
      ["ball", "tree", "color-yellow"],
      "tree",
    ),
    run(
      "bp.l2.j",
      "bp_l2_p_j",
      2,
      ["ball", "dog", "ball", GAP],
      ["dog", "kite", "fish"],
      "dog",
    ),
    run(
      "bp.l2.k",
      "bp_l2_p_k",
      3,
      ["kite", "parrot", "kite", GAP],
      ["cat", "mango", "parrot"],
      "parrot",
    ),
    run(
      "bp.l2.l",
      "bp_l2_p_l",
      4,
      ["color-blue", "ball", "color-blue", GAP],
      ["ball", "mango", "color-blue"],
      "ball",
    ),
    run(
      "bp.l2.m",
      "bp_l2_p_m",
      5,
      ["dog", "tree", "dog", GAP],
      ["tree", "sun", "apple"],
      "tree",
    ),
    run(
      "bp.l2.n",
      "bp_l2_p_n",
      6,
      ["cat", "tree", "cat", GAP],
      ["tree", "kite", "mango"],
      "tree",
    ),
  ],
};

/* ── L3 · read it from both sides ────────────────────────────────────────────
 * `mango · plate · plate · mango · _ · plate`. Longer runs, three shapes of rule
 * (ABB, AAB, ABC), and the gap moved INSIDE — so the piece cannot be worked out
 * by looking left alone. Reading a pattern from both directions at once is the
 * comprehension step this game exists for, and it is the top of a three-level
 * ladder (ADR-037).
 */
const L3: Level = {
  level: 3,
  name: { en: "Keep it going" },
  objective: {
    en: "A hole in the middle — carry the piece that fills it",
  },
  ncf: ["C-8.2"],
  itemCount: 6,
  motorAction: { kind: "grabPlace" },
  items: [
    run(
      "bp.l3.a",
      "bp_l3_p_a",
      0,
      ["mango", "apple", "apple", "mango", GAP, "apple"],
      ["tree", "apple", "kite"],
      "apple",
    ),
    run(
      "bp.l3.b",
      "bp_l3_p_b",
      1,
      ["mango", "tree", "fish", "mango", GAP, "fish"],
      ["tree", "ball", "kite"],
      "tree",
    ),
    run(
      "bp.l3.c",
      "bp_l3_p_c",
      2,
      ["tree", "tree", "ball", "tree", GAP, "ball"],
      ["ball", "tree", "sun"],
      "tree",
    ),
    run(
      "bp.l3.d",
      "bp_l3_p_d",
      3,
      ["sun", "cat", "cat", "sun", GAP, "cat"],
      ["cat", "dog", "parrot"],
      "cat",
    ),
    run(
      "bp.l3.e",
      "bp_l3_p_e",
      4,
      ["apple", "dog", "kite", "apple", GAP, "kite"],
      ["dog", "fish", "tree"],
      "dog",
    ),
    run(
      "bp.l3.f",
      "bp_l3_p_f",
      5,
      ["fish", "fish", "parrot", "fish", GAP, "parrot"],
      ["parrot", "mango", "fish"],
      "fish",
    ),
    run(
      "bp.l3.g",
      "bp_l3_p_g",
      6,
      ["color-red", "mango", "mango", "color-red", GAP, "mango"],
      ["mango", "tree", "color-red"],
      "mango",
    ),
    run(
      "bp.l3.h",
      "bp_l3_p_h",
      0,
      ["mango", "fish", "tree", "mango", GAP, "tree"],
      ["cat", "fish", "ball"],
      "fish",
    ),
    run(
      "bp.l3.i",
      "bp_l3_p_i",
      1,
      ["kite", "kite", "dog", "kite", GAP, "dog"],
      ["dog", "kite", "apple"],
      "kite",
    ),
    run(
      "bp.l3.j",
      "bp_l3_p_j",
      2,
      ["color-yellow", "ball", "ball", "color-yellow", GAP, "ball"],
      ["ball", "mango", "color-yellow"],
      "ball",
    ),
    run(
      "bp.l3.k",
      "bp_l3_p_k",
      3,
      ["cat", "dog", "mango", "cat", GAP, "mango"],
      ["dog", "sun", "parrot"],
      "dog",
    ),
    run(
      "bp.l3.l",
      "bp_l3_p_l",
      4,
      ["mango", "mango", "tree", "mango", GAP, "tree"],
      ["tree", "apple", "mango"],
      "mango",
    ),
    run(
      "bp.l3.m",
      "bp_l3_p_m",
      5,
      ["color-blue", "kite", "kite", "color-blue", GAP, "kite"],
      ["kite", "fish", "color-blue"],
      "kite",
    ),
    run(
      "bp.l3.n",
      "bp_l3_p_n",
      6,
      ["dog", "apple", "parrot", "dog", GAP, "parrot"],
      ["apple", "mango", "cat"],
      "apple",
    ),
  ],
};

export const BRIDGE_PATTERNS: GameContent = {
  id: "bridge-patterns",
  category: "MATCHING",
  contentSkill: Skill.MATCHING,
  /**
   * 🔴 One motor skill, not two. Sky Quest declares COORDINATION **and**
   * BODY_AWARENESS because a jump moves the whole body; a carry is arm and
   * hand. Claiming body awareness for a reach would put a competency on a
   * report card that the game does not exercise.
   */
  motorSkills: [Skill.COORDINATION],
  emoji: "🧩",
  /**
   * 🔴 LOW — and that is a session-level decision, not a description. The
   * pacer alternates high and low exertion across a sitting (games-spec.md §2),
   * so this game is the palate-cleanser after Sky Quest's jumping. It also means
   * §3.6's play-zone prompt does **not** run: a child who is reaching, not
   * jumping, does not need room cleared around them.
   */
  exertion: "low",
  levels: [L1, L2, L3],
};
